import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { pageContext } from 'ctm-util-functions';
import PropTypes from 'prop-types';
import { getFeatureFlag } from 'ctm-selectors/selectors/base';
import { quoteFormSelector } from 'ctm-selectors';
import { AssumedQuestions } from 'ctm-components/components/assumed_questions/assumed_questions';
import { hasAssumedQuestionType } from 'ctm-components/components/utils';
import { useStyles } from './summary_list_styles';
import { SummaryCarDetails } from './summary_sections/summary_car_details';
import { SummaryAccessories } from './summary_sections/summary_accessories';
import { SummaryCoverDetails } from './summary_sections/summary_cover_details';
import { SummaryOvernightAddress } from './summary_sections/summary_overnight_address';
import { SummaryRegularDriver } from './summary_sections/summary_regular_driver';
import { SummaryYoungerDriver } from './summary_sections/summary_younger_driver';
import { SummaryDriverRestrictions } from './summary_sections/summary_driver_restrictions';
import { SummaryContactDetails } from './summary_sections/summary_contact_details';
import { SummaryCommencementDate } from './summary_sections/summary_commencment_date';

const _SummaryList = (props) => {
    const { assumedQuestions, setHasAssumedQuestions, hasAssumedDriverClaims, hasAssumedDriverLicenceType,
        hasAssumedYoungerDriverLicenceType, anyPreviousClaims, isRegularDriverUpdated, hasAssumedDriverComprehensiveInsured,
        hasAsumedDriverComprehensiveInsuredYears } = props;
    const ccquEnabled = getFeatureFlag('newRegularDriverClaims');
    const isXs = pageContext.getProps().pageState.breakpoint === 'xs';
    const classes = useStyles({ isXs, ccquEnabled });
    const assumedQuestionsArray = JSON.parse(JSON.stringify(assumedQuestions));

    // Only rendering summaries when there is no assumed questions for regular or younger driver
    const showSummaryRegularDriver = !(hasAssumedDriverLicenceType || hasAssumedDriverClaims
        || hasAssumedDriverComprehensiveInsured || hasAsumedDriverComprehensiveInsuredYears);
    const showSummaryYoungerDriver = !hasAssumedYoungerDriverLicenceType;

    /*
    * Render summary for regular driver at the very top when there are no assumed questions, but they have
    * anyPreviousClaims as NO or UNSURE, in this event the regular position of the summary is disabled from rendering,
    * until they actually change any of theirs answers for the regular driver.
    * */
    const showNoClaimsSummaryDriver = showSummaryRegularDriver && showSummaryYoungerDriver
        && anyPreviousClaims === 'NO' && !isRegularDriverUpdated;
    /* Watch for array size upon which we update values in state, which is then consumed by subsequent
    * components. Using array size instead of array itself as primitive values are preferred over direct array reference,
    * and in this case we are removing items in the array but not updating existing elements.
    */
    useEffect(() => {
        setHasAssumedQuestions({
            hasAssumedDriverClaims: hasAssumedQuestionType(assumedQuestionsArray, 'regularDriverClaims'),
            hasAssumedDriverLicenceType: hasAssumedQuestionType(assumedQuestionsArray, 'regularDriverLicenceType'),
            hasAssumedYoungerDriverLicenceType: hasAssumedQuestionType(assumedQuestionsArray, 'youngerDriverLicenceType'),
            hasAssumedDriverComprehensiveInsured: hasAssumedQuestionType(assumedQuestionsArray, 'regularDriverComprehensiveInsured'),
            hasAssumedDriverComprehensiveInsuredYears: hasAssumedQuestionType(assumedQuestionsArray, 'regularDriverComprehensiveInsuredYears')
        });
    }, [assumedQuestionsArray.length]);

    return (
        <div className={classes.summary_list_wrapper} data-id={'summaryTable'}>
            <h2 className={classes.summary_list_header}>Summary of your details</h2>
            {showNoClaimsSummaryDriver && <p className={classes.ccquDisclaimer}>Please confirm these details to proceed.</p>}
            {ccquEnabled && <AssumedQuestions />}
            {showNoClaimsSummaryDriver && <SummaryRegularDriver />}
            <SummaryCarDetails />
            <SummaryAccessories />
            <SummaryCoverDetails />
            <SummaryOvernightAddress />
            {showSummaryRegularDriver && !showNoClaimsSummaryDriver && <SummaryRegularDriver />}
            {showSummaryYoungerDriver && <SummaryYoungerDriver />}
            <SummaryDriverRestrictions />
            <SummaryContactDetails />
            <SummaryCommencementDate />
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        state,
        driverClaimsDetails: quoteFormSelector(state).getIn(['coverDetail', 'driver', 'claimsDetails']),
        anyPreviousClaims: quoteFormSelector(state).getIn(['coverDetail', 'driver', 'anyPreviousClaims']),
        isRegularDriverUpdated: state.getIn(['vertical', 'quote', 'form', 'summaryPageEditregular_driver_gender']),
        assumedQuestions: state.getIn(['vertical', 'quote', 'form', 'assumedQuestions']) ?? [],
        hasAssumedDriverClaims: state.getIn(['vertical', 'quote', 'form', 'hasAssumedQuestions', 'hasAssumedDriverClaims']),
        hasAssumedDriverLicenceType: state.getIn(['vertical', 'quote', 'form', 'hasAssumedQuestions', 'hasAssumedDriverLicenceType']),
        hasAssumedYoungerDriverLicenceType: state.getIn(['vertical', 'quote', 'form', 'hasAssumedQuestions', 'hasAssumedYoungerDriverLicenceType']),
        hasAssumedDriverComprehensiveInsured: state.getIn(['vertical', 'quote', 'form', 'hasAssumedQuestions', 'hasAssumedDriverComprehensiveInsured']),
        hasAssumedDriverComprehensiveInsuredYears: state.getIn(['vertical', 'quote', 'form', 'hasAssumedQuestions', 'hasAssumedDriverComprehensiveInsuredYears']),
        formData: state.getIn(['vertical', 'quote', 'form']),
        env: state.getIn(['serverConfig', 'env'])
    };
};

const mapDispatchToProps = (dispatch) => ({
    setHasAssumedQuestions: (value) => {
        dispatch({
            type: 'SET_VALUE',
            value,
            stateKey: 'vertical.quote',
            identifier: 'hasAssumedQuestions'
        });
    }
});

_SummaryList.propTypes = {
    dispatch: PropTypes.func,
    setHasAssumedQuestions: PropTypes.func,
    hasAssumedDriverClaims: PropTypes.bool,
    hasAssumedDriverLicenceType: PropTypes.bool,
    hasAssumedYoungerDriverLicenceType: PropTypes.bool,
    hasAssumedDriverComprehensiveInsured: PropTypes.bool,
    hasAsumedDriverComprehensiveInsuredYears: PropTypes.bool,
    accessToken: PropTypes.string,
    postcode11Month: PropTypes.string,
    journeyId: PropTypes.string,
    productId: PropTypes.string,
    isRegularDriverUpdated: PropTypes.bool
};

const SummaryList = connect(
    mapStateToProps,
    mapDispatchToProps
)(_SummaryList);

export { _SummaryList, SummaryList };
